import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { addYears, format } from 'date-fns';
import * as R from 'ramda';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import differenceInDays from 'date-fns/differenceInDays';
import LocationsForm from './LocationsForm';
import TrailerStatusChip, { TRAILER_STATUS_TIP } from '../../components/TrailerStatusChip';
import { useRemoveTrailerMutation } from '../../trailerStatusEndpoints';

const notEmpty = R.compose(R.not, R.isEmpty);

const TrailerDetails = ({
  effectiveDate,
  addNewLocation,
  hasMexicanTrailerNumber,
  isEditing,
  isExpired,
  lastLoadedDate,
  locations,
  locationState,
  mexicanTrailerNumber,
  removeNewLocation,
  selectableLocations,
  setLocationState,
  setTrailerState,
  status,
  trailerId,
  vaporTightnessCode,
}) => {
  const [validationState, setValidationState] = useState({
    mexicanTrailerNumber: '',
  });

  const displayStatusTip = ['GRACE', 'DEACTIVATED'].includes(status);

  const renderDates = () => {
    const effectiveDateLabel = effectiveDate
      ? format(new Date(effectiveDate), 'MMMM dd, yyyy')
      : '';

    // eslint-disable-next-line no-shadow, consistent-return
    const expirationDateLabel = effectiveDate => {
      if (effectiveDate) {
        const formattedExpirationDate = new Date(effectiveDate);
        const futureExpirationDate = addYears(formattedExpirationDate, 1);
        const expirationDateString = format(new Date(futureExpirationDate), 'MMMM dd, yyyy');
        return expirationDateString;
      }
      return '';
    };

    return (
      <>
        <Typography gutterBottom variant="caption">
          Certified Through
        </Typography>
        <Typography sx={{ mb: 2 }} variant="body1bold">
          {effectiveDateLabel} - {expirationDateLabel(effectiveDate)}
        </Typography>
      </>
    );
  };
  const handleHasMexicanTrailerNumberChange = R.compose(
    setTrailerState,
    checked =>
      R.mergeLeft({
        hasMexicanTrailerNumber: checked,
        mexicanTrailerNumber: '',
      }),
    R.path(['target', 'checked'])
  );

  const validateMexicanTrailerNumber = R.compose(
    setValidationState,
    R.assoc('mexicanTrailerNumber'),
    R.ifElse(R.isEmpty, R.always('Mexican Trailer Number required'), R.always(''))
  );

  const handleMexicanTrailerNumberChange = R.compose(
    setTrailerState,
    R.assoc('mexicanTrailerNumber'),
    R.tap(validateMexicanTrailerNumber),
    R.path(['target', 'value'])
  );

  const [submit] = useRemoveTrailerMutation();

  const handleRemoveOriginalSelection = location => {
    submit({ locationId: location.locationId, trailerId });
  };

  return (
    <Stack sx={{ p: 3 }}>
      {isEditing && (
        <Stack direction="row" sx={{ alignItems: 'center', mb: 2.25 }}>
          <FormControlLabel
            control={
              <Switch
                checked={hasMexicanTrailerNumber}
                onChange={handleHasMexicanTrailerNumberChange}
              />
            }
            label="MX Customs Trailer Number"
          />
          <Tooltip
            placement="top"
            slotProps={{
              popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
            }}
            title="Optional trailer number used when crossing the Mexican/US border."
          >
            <InfoRoundedIcon sx={{ '&:hover': { cursor: 'pointer' }, color: 'graphic.main' }} />
          </Tooltip>
        </Stack>
      )}
      <Typography gutterBottom variant="caption">
        Trailer ID
      </Typography>
      <Typography sx={{ mb: 1 / 2 }} variant="body1bold">
        {vaporTightnessCode}
      </Typography>
      <Stack alignItems="center" direction="row" spacing={2} sx={{ mb: 2 }}>
        {status && <TrailerStatusChip status={status} />}
        {displayStatusTip && (
          <Tooltip
            placement="top"
            slotProps={{
              popper: { sx: { p: 1, textAlign: 'center' } },
            }}
            title={TRAILER_STATUS_TIP[status]}
          >
            <InfoRoundedIcon sx={{ '&:hover': { cursor: 'pointer' }, color: 'graphic.main' }} />
          </Tooltip>
        )}
      </Stack>
      {isEditing && hasMexicanTrailerNumber && (
        <TextField
          error={notEmpty(validationState.mexicanTrailerNumber)}
          fullWidth
          helperText={validationState.mexicanTrailerNumber}
          label="MX Customs Trailer Number"
          onChange={handleMexicanTrailerNumberChange}
          sx={{ mb: 2, mt: 1 }}
          value={mexicanTrailerNumber}
        />
      )}
      {!isEditing && mexicanTrailerNumber && (
        <>
          <Typography gutterBottom variant="caption">
            MX Customs Trailer Number
          </Typography>
          <Typography sx={{ mb: 2 }} variant="body1bold">
            {mexicanTrailerNumber}
          </Typography>
        </>
      )}
      {!isExpired && renderDates()}
      {isEditing && ( // not rendered for create-trailer
        <>
          <Typography gutterBottom variant="caption">
            Locations
          </Typography>
          {locations.map(location => {
            const labelId = `checkbox-list-label-${location.locationId}`;
            return (
              <Fragment key={location.locationId}>
                <ListItem disablePadding key={location.locationId}>
                  <ListItemButton
                    dense
                    onClick={() => handleRemoveOriginalSelection(location)}
                    role={undefined}
                    sx={{ px: 3 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <Checkbox
                        checked={R.includes(location, locations)}
                        disableRipple
                        edge="start"
                        tabIndex={-1}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={`${location.locationId} ${location.name}`}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </Fragment>
            );
          })}
          <LocationsForm
            locations={locationState.locations}
            locationState={locationState}
            onAdd={addNewLocation}
            onRemove={removeNewLocation}
            selectableLocations={selectableLocations}
            setLocationState={setLocationState}
          />
          <Typography gutterBottom variant="caption">
            Last Loaded
          </Typography>
          <Typography variant="body1bold">
            {lastLoadedDate
              ? `${differenceInDays(new Date(), new Date(lastLoadedDate))} Days`
              : '--'}
          </Typography>
        </>
      )}
    </Stack>
  );
};

TrailerDetails.propTypes = {
  addNewLocation: PropTypes.func,
  effectiveDate: PropTypes.string,
  hasMexicanTrailerNumber: PropTypes.bool,
  isEditing: PropTypes.bool,
  isExpired: PropTypes.bool,
  lastLoadedDate: PropTypes.string,
  locationState: PropTypes.arrayOf(PropTypes.shape({})),
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mexicanTrailerNumber: PropTypes.string,
  removeNewLocation: PropTypes.func,
  selectableLocations: PropTypes.arrayOf(PropTypes.shape({})),
  setLocationState: PropTypes.func,
  setTrailerState: PropTypes.func,
  status: PropTypes.string,
  trailerId: PropTypes.string,
  vaporTightnessCode: PropTypes.string.isRequired,
};
TrailerDetails.defaultProps = {
  addNewLocation: null,
  effectiveDate: '',
  hasMexicanTrailerNumber: false,
  isEditing: false,
  isExpired: false,
  lastLoadedDate: null,
  locationState: null,
  mexicanTrailerNumber: '',
  removeNewLocation: null,
  selectableLocations: null,
  setLocationState: null,
  setTrailerState: null,
  status: '',
  trailerId: '',
};

export default TrailerDetails;
